import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { ContractTypesDocument, JobLocationsDocument, WorkingTimeModelsDocument } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";
import { ProjectTags } from "@/pages/project/ProjectTags";
import { parseTags } from "@/pages/project/project-plan";
import type { DashboardData } from "@/pages/project/use-dashboard-source";
import { ListingField } from "./ListingField";

export function ListingTags({ data, onChange, readOnly = true }: { data: DashboardData; onChange?: (patch: DashboardData) => void; readOnly?: boolean }) {
  const intl = useIntl();
  const locations = useQuery(JobLocationsDocument, { skip: readOnly && !data.jobLocationId });
  const contracts = useQuery(ContractTypesDocument, { skip: readOnly && !data.contractTypeId });
  const times = useQuery(WorkingTimeModelsDocument, { skip: readOnly && !data.workingTimeModelId });
  const catalogs = [
    { key: "workingTimeModelId", label: "profile.page.label.workingTimeModel", query: times, values: times.data?.workingTimeModels.workingTimeModels },
    { key: "contractTypeId", label: "profile.page.label.contractType", query: contracts, values: contracts.data?.contractTypes.contractTypes },
    { key: "jobLocationId", label: "profile.page.label.jobLocation", query: locations, values: locations.data?.jobLocations.jobLocations },
  ];
  const legacy = catalogs.filter(catalog => typeof data[catalog.key] === "string" && data[catalog.key]);
  const pendingLegacy = legacy.some(catalog => catalog.query.loading || catalog.query.error);
  const tags = [...new Set([...parseTags(data.tags), ...legacy.flatMap(catalog => {
    if (catalog.query.loading || catalog.query.error) return [];
    const value = catalog.values?.find(value => value.id === data[catalog.key]);
    return value?.localizedName?.localized || value?.name || intl.formatMessage({ id: catalog.label }) + ": " + intl.formatMessage({ id: "share.listings.metadata.unavailable" });
  })])];
  const suggestions = [...new Set(catalogs.flatMap(catalog => (catalog.values ?? []).map(value => value.localizedName?.localized || value.name).filter((value): value is string => !!value)))].sort((a, b) => a.localeCompare(b, intl.locale));
  const error = catalogs.find(catalog => catalog.query.error)?.query.error;
  if (readOnly && !tags.length && !pendingLegacy) return null;
  return <ListingField label={<FormattedMessage id="project.meta.tags" />}>
    <ProjectTags tags={tags} suggestions={suggestions} readOnly={readOnly || !!pendingLegacy}
      emptyHint={<FormattedMessage id="share.listings.tags.empty" />}
      onChange={next => onChange?.({ tags: next, ...Object.fromEntries(legacy.map(catalog => [catalog.key, null])) })} />
    {pendingLegacy && !error ? <p className="smycles-field-meta"><FormattedMessage id="common.loading" /></p> : null}
    {error ? <p role="alert" className="smycles-field-meta">{userErrorMessage(intl, error)}</p> : null}
  </ListingField>;
}
